// This file implements a custom base query for RTK Query with dual token authentication:
// 1. OAuth token (client credentials) for unauthenticated endpoints (login, password reset, etc.)
// 2. Access token (user session) for authenticated endpoints (protected resources)
//
// Key features:
// - Automatic token refresh when access token expires
// - Mutex locks to prevent race conditions during token refresh
// - Intelligent error handling with appropriate redirects
// - Session management and cleanup

import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import API_URLS, { API_BASE_URL } from "@constants/apiUrls";
import { RootState } from "redux/store";
import { checkTokenExpired } from "@utils/common";
import * as cookie from "cookie";
import {
  setAccessToken,
  logout,
  setoAuthAccessToken,
} from "@redux/slices/AuthSlice";
import { Mutex } from "async-mutex";
import { TokenRefreshResponseDataInterface } from "interfaces/common.interface";
import { notifyUser } from "@redux/slices/CommonSlice";
import { PAGE_URLS } from "@constants/common";

// ====================================================================================
// CONFIGURATION: OAuth Endpoints and URLs
// ====================================================================================

// List of endpoint names that should use OAuth token instead of user access token
// These are typically public/unauthenticated endpoints like login, signup, password reset
const OAUTH_ENDPOINTS = [
  "login",
  "verifyEmail",
  "getToken",
  "ForgotPasswordOtp",
  "ResetPassword",
  "verifyOtpForgotPassword",
  "resendOtpForgotPassword",
  "verifyOtpLogin",
  "verifyOtpForgotPassword",
  "resendOtpLogin",
  "resendOtpForgotPassword",
];

// Fallback list: URLs that should use OAuth token (used if endpoint name is not available)
// This provides a safety net for detecting OAuth requests by their URL path
const OAUTH_URLS = [
  API_URLS.LOGIN,
  API_URLS.VERIFY_USER,
  API_URLS.OAUTH_TOKEN,
  API_URLS.RESET_PASSWORD_REQUEST,
  API_URLS.RESET_PASSWORD_SUBMIT,
  API_URLS.VERIFY_FORGOT_PASSWORD_OTP,
  API_URLS.RESEND_FORGOT_PASSWORD_OTP,
  API_URLS.VERIFY_OTP,
  API_URLS.VERIFY_FORGOT_PASSWORD_OTP,
  API_URLS.RESEND_OTP,
  API_URLS.RESEND_FORGOT_PASSWORD_OTP,
];

// ====================================================================================
// BASE QUERY SETUP
// ====================================================================================

// Configure the base query with automatic Authorization header injection
// This function runs before every API request and adds the appropriate token
const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (
    headers: Headers,
    { getState, endpoint }: { getState: () => unknown; endpoint?: string },
  ) => {
    const state = getState() as RootState;

    // For OAuth endpoints: attach OAuth token (used for public/unauthenticated requests)
    if (endpoint && OAUTH_ENDPOINTS.includes(endpoint)) {
      if (state.auth.oAuthToken) {
        headers.set("Authorization", `Bearer ${state.auth.oAuthToken}`);
      }
    } else {
      // For authenticated endpoints: attach user's access token (requires login)
      if (state.auth.accessToken) {
        headers.set("Authorization", `Bearer ${state.auth.accessToken}`);
      }
    }
    return headers;
  },
});

// ====================================================================================
// MUTEX LOCKS - Prevent Race Conditions
// ====================================================================================

// Mutex for access token refresh - ensures only one refresh happens at a time
// Prevents multiple simultaneous API calls from triggering duplicate refresh requests
const accessMutex = new Mutex();

// Mutex for OAuth token acquisition - ensures only one OAuth token request at a time
const oAuthMutex = new Mutex();

// ====================================================================================
// RATE LIMITING - Prevent Rapid Duplicate Refreshes
// ====================================================================================

// Timestamp of the last token refresh attempt
let lastRefreshAttempt = 0;

// Minimum time (in milliseconds) between consecutive refresh attempts
// Prevents rapid-fire refresh requests that could overwhelm the server
const REFRESH_COOLDOWN = 1000; // 1 second cooldown

// ====================================================================================
// PUBLIC PATHS - Prevent Redirect Loops
// ====================================================================================

// List of public pages where users should not be redirected on auth errors
// These pages are accessible without authentication
const PUBLIC_PATHS = [
  "/login",
  "/session-expired",
  "/forgot-password",
  "/reset-password",
  "/400",
  "/403",
  "/404",
];

// Check if the current page is a public path (to prevent redirect loops)
const isOnPublicPath = () =>
  PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

// ====================================================================================
// HELPER FUNCTIONS
// ====================================================================================

/**
 * Determines if a request should use OAuth token instead of access token
 *
 * Logic:
 * 1. First tries to match by endpoint name (most reliable)
 * 2. Falls back to URL matching if endpoint name is not available
 *
 * @param args - The request arguments (URL or FetchArgs object)
 * @param extraOptions - Additional options that may contain endpoint name
 * @returns true if this is an OAuth request, false otherwise
 */
const isOAuthRequest = (
  args: string | FetchArgs,
  extraOptions: any,
): boolean => {
  // Prefer endpoint name provided by RTK Query (prepareHeaders receives it)
  const endpointName = extraOptions?.endpoint;
  if (endpointName && OAUTH_ENDPOINTS.includes(endpointName)) return true;

  // Fallback: inspect URL
  const url =
    typeof args === "string"
      ? args
      : typeof (args as FetchArgs).url === "string"
        ? (args as FetchArgs).url
        : "";

  return OAUTH_URLS.some((u) => url.includes(u));
};

/**
 * Clears user session data and redirects to specified path
 *
 * This function:
 * 1. Removes access token expiry cookie
 * 2. Dispatches logout action to clear Redux state
 * 3. Redirects to specified path (only if not already on a public page)
 *
 * @param api - RTK Query API object for dispatching actions
 * @param redirectPath - Path to redirect to (default: /session-expired)
 */
const clearSessionAndRedirect = (api: any, redirectPath = PAGE_URLS.LOGIN) => {
  // Remove the access token expiry cookie by setting it to expired date
  document.cookie =
    "access_token_expiry=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  // Clear Redux auth state
  api.dispatch(logout());

  // Only redirect if not already on a public page (prevents loops)
  if (!isOnPublicPath()) {
    window.location.replace(redirectPath);
  }
};

/**
 * Acquires a new OAuth token using client credentials flow
 *
 * OAuth tokens are used for unauthenticated endpoints (login, signup, etc.)
 * This uses the client credentials grant type with client_id and client_secret
 *
 * @param api - RTK Query API object for dispatching actions
 * @param extraOptions - Additional options passed to baseQuery
 * @returns The new OAuth access token or null if acquisition fails
 */
const acquireOAuthToken = async (api: any, extraOptions: any) => {
  try {
    // Prepare form data with client credentials from environment variables
    const formData = new FormData();
    formData.append(
      "client_id",
      import.meta.env.VITE_MYCREDITS_CONSOLE_OAUTH_CLIENT_ID || "",
    );
    formData.append(
      "client_secret",
      import.meta.env.VITE_MYCREDITS_CONSOLE_OAUTH_CLIENT_SECRET || "",
    );
    formData.append("grant_type", "client_credentials");

    // Make the OAuth token request
    const result = await baseQuery(
      {
        url: API_BASE_URL + API_URLS.OAUTH_TOKEN,
        method: "POST",
        body: formData,
      },
      api,
      extraOptions,
    );

    // If successful, store the OAuth token in Redux
    if (result.data && typeof result.data === "object") {
      const { access_token } = result.data as { access_token: string };
      api.dispatch(setoAuthAccessToken(access_token));
      return access_token;
    }
  } catch (e) {
    console.error("OAuth token acquisition failed:", e);
  }
  return null;
};

/**
 * Refreshes the user's access token using the refresh token
 *
 * This function:
 * 1. Checks if a refresh was attempted recently (cooldown period)
 * 2. Makes a refresh token request (refresh token is sent via HTTP-only cookie)
 * 3. Stores the new access token in Redux
 *
 * @param api - RTK Query API object for dispatching actions
 * @param extraOptions - Additional options passed to baseQuery
 * @returns The new access token or null if refresh fails
 */
const refreshAccessToken = async (api: any, extraOptions: any) => {
  const now = Date.now();

  // Prevent rapid duplicate refresh attempts (within cooldown period)
  // This avoids overwhelming the server with multiple refresh requests
  if (now - lastRefreshAttempt < REFRESH_COOLDOWN) {
    console.log("Skipping refresh - recent attempt detected");
    // Wait a bit for the ongoing refresh to complete
    await new Promise((resolve) => setTimeout(resolve, 100));
    return null;
  }

  // Update last refresh attempt timestamp
  lastRefreshAttempt = now;

  try {
    // Make the token refresh request
    // Note: refresh token is automatically sent via HTTP-only cookie (credentials: "include")
    const result = await baseQuery(
      {
        url: API_BASE_URL + API_URLS.REFRESH_TOKEN,
        method: "POST",
        credentials: "include", // Send cookies with the request
      },
      api,
      extraOptions,
    );

    // If successful, store the new access token in Redux
    if (result.data && typeof result.data === "object") {
      const { data } = result as TokenRefreshResponseDataInterface;
      api.dispatch(setAccessToken(data.access));
      return data.access;
    }
    if (result?.error?.status === 400) {
      clearSessionAndRedirect(api, PAGE_URLS.LOGIN);
      api.dispatch(
        notifyUser({
          title: "Session expired",
          message: "Your session has expired. Please log in again.",
          severity: "error",
        }),
      );
    }
  } catch (e) {
    console.error("Access token refresh failed:", e);
  }
  return null;
};

/**
 * Centralized error handler for API responses
 *
 * Handles different HTTP status codes:
 * - 401 (Unauthorized): Clear session and redirect, or return error for OAuth requests
 * - 403 (Forbidden): Redirect to forbidden page
 * - 429 (Too Many Requests): Show error notification
 *
 * @param result - The API response result
 * @param api - RTK Query API object for dispatching actions
 * @param isOAuth - Whether this is an OAuth request
 * @returns Modified result object or undefined to use original result
 */
const handleErrorResponse = (
  result: any,
  api: any,
  isOAuth: boolean,
): any | undefined => {
  // Only handle error responses
  if (!result?.error) return;

  const status = result.error.status;
  switch (status) {
    case 401:
      // For OAuth requests: return error without redirecting (let UI handle it)
      // This is important because OAuth errors should be handled by login forms
      if (isOAuth) {
        return {
          error: {
            status: 401,
            data: result.error.data || { message: "Unauthorized" },
          },
        };
      }
      // For authenticated requests: show notification, clear session, and redirect to login
      clearSessionAndRedirect(api, PAGE_URLS.LOGIN);
      api.dispatch(
        notifyUser({
          title: "Session expired",
          message: " Your session has expired. Please log in again.",
          severity: "error",
        }),
      );

      return {
        error: { status: 401, data: { message: "Session expired" } },
      };
    case 403:
      // Forbidden: redirect to 403 page (only if not already on a public page)
      if (!isOnPublicPath()) window.location.replace(PAGE_URLS.ACCESS_DENIED);
      return { error: { status: 403, data: { message: "Forbidden" } } };
    case 500:
      window.location.replace(PAGE_URLS.ERROR_500);
      return { error: { status: 500, data: { message: "Server Error" } } };
    case 429:
      // Rate limit exceeded: show error notification to user
      api.dispatch(
        notifyUser({
          message: (result.error as any).data?.message || "Too many requests",
          severity: "error",
        }),
      );
      return result;
  }
  return result;
};

// ====================================================================================
// MAIN BASE QUERY WITH AUTHENTICATION
// ====================================================================================

/**
 * Enhanced base query with automatic token management
 *
 * This is the core function that wraps all API requests with authentication logic:
 *
 * Flow for OAuth requests (unauthenticated endpoints):
 * 1. Check if OAuth token exists, acquire if needed
 * 2. Make the API request with OAuth token
 * 3. Handle errors (without session clearing)
 *
 * Flow for authenticated requests:
 * 1. Check if access token is expired or missing
 * 2. If expired, refresh the token (with mutex lock to prevent duplicates)
 * 3. Make the API request with access token
 * 4. Handle errors (may clear session and redirect)
 *
 * @param args - The request arguments (URL or FetchArgs object)
 * @param api - RTK Query API object
 * @param extraOptions - Additional options
 * @returns The API response or error
 */
const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const isOAuth = isOAuthRequest(args, extraOptions);

  // ====================================================================================
  // BRANCH 1: OAuth Flow (Unauthenticated Endpoints)
  // ====================================================================================
  // These endpoints don't require user login - they use client credentials OAuth token
  // Examples: login, signup, forgot password, email verification
  if (isOAuth) {
    // If OAuth token doesn't exist, acquire one
    if (!state.auth.oAuthToken) {
      // Use mutex to ensure only one OAuth token acquisition happens at a time
      if (!oAuthMutex.isLocked()) {
        const release = await oAuthMutex.acquire();
        try {
          const newOAuth = await acquireOAuthToken(api, extraOptions);
          if (!newOAuth) {
            // Failed to get OAuth token - return error
            return {
              error: {
                status: 401,
                data: { message: "Failed to get OAuth token" },
              },
            };
          }
        } finally {
          // Always release the mutex
          release();
        }
      } else {
        // Another request is already acquiring OAuth token - wait for it
        await oAuthMutex.waitForUnlock();
      }
    }

    // Make the API request with OAuth token (attached by prepareHeaders)
    const oResult = await baseQuery(args, api, extraOptions);

    // Handle any errors (note: isOAuth=true prevents session clearing)
    const handled = handleErrorResponse(oResult, api, true);
    return handled || oResult;
  }

  // ====================================================================================
  // BRANCH 2: Authenticated Flow (User Access Token)
  // ====================================================================================
  // These endpoints require user to be logged in - they use user's access token

  // Check token expiration from cookie
  const { access_token_expiry } = cookie.parse(document.cookie);
  const accessToken = state.auth.accessToken;
  const expired = checkTokenExpired(access_token_expiry);

  // If token is expired or missing, and no refresh is in progress, refresh it
  if ((expired || !accessToken) && !accessMutex.isLocked()) {
    const release = await accessMutex.acquire();
    try {
      // Double-check after acquiring mutex (another request might have already refreshed)
      // This prevents unnecessary duplicate refresh requests
      const { access_token_expiry: currentCookie } = cookie.parse(
        document.cookie,
      );
      const currentToken = (api.getState() as RootState).auth.accessToken;

      // Only refresh if still needed (token still expired/missing)
      if (checkTokenExpired(currentCookie) || !currentToken) {
        const newAccess = await refreshAccessToken(api, extraOptions);
        if (!newAccess) {
          // Refresh failed - clear session and redirect
          clearSessionAndRedirect(api);
          return {
            error: { status: 401, data: { message: "Session expired" } },
          };
        }
      }
    } finally {
      // Always release the mutex
      release();
    }
  } else if (accessMutex.isLocked()) {
    // Another request is already refreshing the token - wait for it to complete
    await accessMutex.waitForUnlock();
  }

  // Make the API request with (possibly refreshed) access token
  const result = await baseQuery(args, api, extraOptions);

  // Handle any errors (may clear session and redirect for 401s)
  const handled = handleErrorResponse(result, api, false);
  return handled || result;
};

// ====================================================================================
// RTK QUERY API EXPORT
// ====================================================================================

/**
 * Main RTK Query API instance
 *
 * This is imported by all API slice files to create endpoints
 * It uses the enhanced base query with automatic authentication
 *
 * Usage in other files:
 * import { api } from './customBaseQuery';
 * export const userApi = api.injectEndpoints({ endpoints: ... });
 */
export const api = createApi({
  reducerPath: "api",
  tagTypes: ["Promotions", "Users", "Drafts"],
  baseQuery: baseQueryWithReAuth,
  endpoints: () => ({}), // Endpoints are injected in separate files
});
